import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { HttpRequestService } from 'src/app/core/services';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ExamCategory } from 'src/app/core/interfaces/exam-category';
import { TestSeries } from 'src/app/core/interfaces/test-series';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-add-update-dashboard-config',
  templateUrl: './add-update-dashboard-config.component.html',
  styleUrls: ['./add-update-dashboard-config.component.scss'],
})
export class AddUpdateDashboardConfigComponent implements OnInit {
  /** Forms */
  featureCategoryForm: FormGroup;
  featureTestSeriesForm: FormGroup;

  buttonLoading = false;
  categoryButtonLoading = false;
  idForUpdate: string;

  /** Category */
  allExamCategories: ExamCategory[] = [];
  selectedExamCategory: any[] = [];
  newSelectedExamCategories: any[] = [];
  filterExamCategory: any[] = [];
  loading = false;
  pageIndex = 1;
  pageSize = 100;
  limit = false;
  examCategorySearch = '';

  /** Test Series */
  allTestSeries: TestSeries[] = [];
  selectedTestSeries: any[] = [];
  newSelectedTestSeries: any[] = [];
  filterTestSeries: any[] = [];
  testSeriesLoading = false;
  testSeriesPageIndex = 1;
  testSeriesPageSize = 100;
  testSeriesLimit = false;
  testSeriesSearch: Subject<string> = new Subject<string>();
  search = '';
  constructor(
    private fb: FormBuilder,
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private activatedRoute: ActivatedRoute,
  ) {
    this.idForUpdate = this.activatedRoute.snapshot.params.id;

    this.featureCategoryForm = this.fb.group({
      featureCategory: [[], Validators.required],
    });

    this.featureTestSeriesForm = this.fb.group({
      featureTestSeries: [[], Validators.required],
    });
  }

  ngOnInit(): void {
    this.testSeriesSearch
      .pipe(debounceTime(1000))
      .subscribe((success: string) => {
        this.search = success;
        this.getAllTestSeriesData();
      });
    this.getAllFeatureCategory();
    this.getAllExamsCategory();
    this.getAllFeatureTestSeries();
    this.getAllTestSeriesData();
  }

  /* ================= CATEGORY ================= */

  dropCategory(event: CdkDragDrop<string[]>): void {
    moveItemInArray(
      this.selectedExamCategory,
      event.previousIndex,
      event.currentIndex,
    );
    this.newSelectedExamCategories = [...this.selectedExamCategory];
  }

  getAllExamsCategory(skip = 0): void {
    const params: any = {
      skip,
      limit: this.pageSize,
      status: true,
    };

    if (this.examCategorySearch) params.search = this.examCategorySearch;

    this.loading = true;

    this.httpRequestService
      .request('get', 'exam-categories', params)
      .subscribe((res: any) => {
        this.loading = false;
        this.allExamCategories.push(...res.data);

        if (this.allExamCategories.length >= res.totalCount) {
          this.limit = true;
        }

        this.filterCategoryValues();
      });
  }

  examCategoryPagination(): void {
    if (this.limit) return;
    this.pageIndex++;
    this.getAllExamsCategory(this.pageSize * (this.pageIndex - 1));
  }

  getAllFeatureCategory(): void {
    this.httpRequestService
      .request('get', 'dashboards/feature-categories')
      .subscribe((res: any) => {
        if (res.data?.length) {
          this.selectedExamCategory = [...res.data[0].featureCategory];
          this.newSelectedExamCategories = [...this.selectedExamCategory];
          this.filterCategoryValues();
        }
      });
  }

  filterCategoryValues(): void {
    this.filterExamCategory = this.allExamCategories
      .filter(
        (item) => !this.selectedExamCategory.some((x) => x._id === item._id),
      )
      .map((item) => ({ ...item, isChecked: false }));
  }

  addCategories(): void {
    const toAdd = this.filterExamCategory
      .filter((x) => x.isChecked)
      .map((x) => ({ ...x, isChecked: false })); // copy & reset checkbox

    this.selectedExamCategory.push(...toAdd);
    this.newSelectedExamCategories = [...this.selectedExamCategory];
    this.filterCategoryValues();
  }

  removeCategories(): void {
    this.selectedExamCategory = this.selectedExamCategory.filter(
      (x) => !x.isChecked,
    );
    this.newSelectedExamCategories = [...this.selectedExamCategory];
    this.filterCategoryValues();
  }

  submitFeatureCategories(): void {
    if (!this.selectedExamCategory.length) {
      this.notificationService.error('', 'Add some Category');
      return;
    }

    this.featureCategoryForm.patchValue({
      featureCategory: this.selectedExamCategory.map((x) => x._id),
    });

    this.categoryButtonLoading = true;

    this.httpRequestService
      .request(
        'put',
        'dashboards/feature-categories',
        this.featureCategoryForm.value,
      )
      .subscribe(
        () => {
          this.notificationService.success('', 'Categories Updated');
          this.categoryButtonLoading = false;
        },
        (err) => this.handleError(err),
      );
  }

  /* ================= TEST SERIES ================= */

  dropTestSeries(event: CdkDragDrop<string[]>): void {
    moveItemInArray(
      this.selectedTestSeries,
      event.previousIndex,
      event.currentIndex,
    );
    this.newSelectedTestSeries = [...this.selectedTestSeries];
  }
  getAllTestSeriesData(skip = 0): void {
    const params: any = {
      skip,
      limit: this.testSeriesPageSize,
    };
    console.log(this.search);
    if (this.search) {
      params.search = this.search;
    } else {
      delete params.search;
    }
    if (skip === 0) {
      this.allTestSeries = [];
      this.testSeriesLimit = false;
    }

    this.httpRequestService
      .request('get', 'test-series/for-list', params)
      .subscribe((res: any) => {
        this.testSeriesLoading = false;
        this.filterTestSeries = [];
        this.allTestSeries.push(...res.data);
        if (this.allTestSeries.length >= res.totalCount) {
          this.testSeriesLimit = true;
        }

        this.filterTestSeriesValues();
      });
  }

  testSeriesPagination(): void {
    if (this.testSeriesLimit) return;
    this.testSeriesPageIndex++;
    this.getAllTestSeriesData(
      this.testSeriesPageSize * (this.testSeriesPageIndex - 1),
    );
  }

  getAllFeatureTestSeries(): void {
    this.httpRequestService
      .request('get', 'dashboards/feature-test-series')
      .subscribe((res: any) => {
        if (res.data?.length) {
          this.selectedTestSeries = [...res.data[0].featureTestSeries];
          this.newSelectedTestSeries = [...this.selectedTestSeries];
          this.filterTestSeriesValues();
        }
      });
  }

  filterTestSeriesValues(): void {
    this.filterTestSeries = this.allTestSeries
      .filter(
        (item) => !this.selectedTestSeries.some((x) => x._id === item._id),
      )
      .map((item) => ({ ...item, isChecked: false }));
  }

  addTestSeries(): void {
    const toAdd = this.filterTestSeries
      .filter((x) => x.isChecked)
      .map((x) => ({ ...x, isChecked: false })); // copy & reset checkbox

    this.selectedTestSeries.push(...toAdd);
    this.newSelectedTestSeries = [...this.selectedTestSeries];
    this.filterTestSeriesValues();
  }

  removeTestSeries(): void {
    this.selectedTestSeries = this.selectedTestSeries.filter(
      (x) => !x.isChecked,
    );
    this.newSelectedTestSeries = [...this.selectedTestSeries];
    this.filterTestSeriesValues();
  }

  submitFeatureTestSeries(): void {
    if (!this.selectedTestSeries.length) {
      this.notificationService.error('', 'Add some Test Series');
      return;
    }

    this.featureTestSeriesForm.patchValue({
      featureTestSeries: this.selectedTestSeries.map((x) => x._id),
    });

    this.buttonLoading = true;

    this.httpRequestService
      .request(
        'put',
        'dashboards/feature-test-series',
        this.featureTestSeriesForm.value,
      )
      .subscribe(
        () => {
          this.notificationService.success('', 'Test Series Updated');
          this.buttonLoading = false;
        },
        (err) => this.handleError(err),
      );
  }

  private handleError(error: any): void {
    this.buttonLoading = false;
    this.notificationService.error(
      '',
      error?.error?.message || 'Something went wrong',
    );
  }

  searchTestSeries(event: any): void {
    this.testSeriesSearch.next(event);
  }
}
